import { getRoot, Instance, SnapshotOut, types } from "mobx-state-tree"
import {
  Event,
  UserLastSeenData,
  UserLastSeenList,
  UserList,
  UserProfile,
} from "~proto"
import { withEnvironment } from "../extensions/with-environment"
import { User, UserModel } from "../user/user"
import { RootStoreModel } from "~models/root-store"

/**
 * Model description here for TypeScript hints.
 */
export const UserStoreModel = types
  .model("UserStore")
  .props({
    users: types.optional(types.map(UserModel), {}),
    currentUser: types.maybe(types.safeReference(UserModel)),
  })
  .extend(withEnvironment)
  .views((self) => ({
    getDisplayNameByUser(user: User) {
      const { contactStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const contact = contactStore.byUserId(user.id)
      return contact ? contact.displayName : user.displayName
    },
    getUserByUsername(searchUsername: string) {
      return Array.from(self.users.values()).find(({ username }) => username === searchUsername)
    },
    get usersByName() {
      const { userStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const dst = {}
      Array.from(self.users.values())
        .filter((user: User) => user.id !== userStore.currentUser.id)
        .forEach((user: User) => {
          const nameLetter = user?.name?.substring(0, 1)
          dst[nameLetter] = dst[nameLetter] || []
          dst[nameLetter].push(user)
        })
      const lst = Object.keys(dst).map((letter) => ({ letter, data: dst[letter] }))
      return lst
    },
  }))
  .actions((self) => {
    function updateUser({ id, ...values }: UserProfile) {
      const user = self.users.get(id.toString()) // 根据ID获取需要更新资料的用户
      user.update(values)
      return user
    }
    // event-store
    function onUserUpdated({ data: user }: Event<UserProfile>) {
      return updateUser(user)
    }
    function addUser(user: UserProfile) {
      return self.users.has((user.id || user.user_id).toString()) ? updateUser(user) : self.users.put(user)
    }
    function setCurrentUser(user: User) {
      self.currentUser = user
      return self.currentUser
    }
    // event-store
    function onUserProfile({ data }: Event<UserProfile>) {
      // console.log("onUserProfile", data)
      setCurrentUser(addUser(data))
    }
    // event-store
    function onUserList({ data: { items } }: Event<UserList>) {
      items.forEach(addUser)
    }
    function updateUserLastSeen(data: UserLastSeenData) {
      const user = self.users.get(data.user_id.toString())
      if (user) user.onLastSeen({ data })
    }
    // event-store
    function onUserLastSeen({ data }: Event<UserLastSeenData>) {
      updateUserLastSeen(data)
    }
    // event-store
    function onUserLastSeenList({ data: { items } }: Event<UserLastSeenList>) {
      items.forEach(updateUserLastSeen)
    }
    return {
      setCurrentUser,
      addUser,
      onUserProfile,
      onUserList,
      onUserUpdated,
      onUserLastSeen,
      onUserLastSeenList,
    }
  })

type UserStoreType = Instance<typeof UserStoreModel>
export interface UserStore extends UserStoreType {}
type UserStoreSnapshotType = SnapshotOut<typeof UserStoreModel>
export interface UserStoreSnapshot extends UserStoreSnapshotType {}
export const createUserStoreDefaultModel = () => types.optional(UserStoreModel, {})
